// ============================================================
// Navigation API Service
// Connects to /api/v1/navigation endpoints
// ============================================================

import { httpClient, ApiError } from './http-client';

export interface NavigationItem {
  id: string;
  label_key: string;
  icon: string;
  href: string;
  permission: string;
  badge?: string;
  badge_color?: string;
  section: 'main' | 'bottom';
  status: 'active' | 'inactive';
  display_order: number;
  ready_for_internal_marketing: boolean;
  ready_for_early_adopters: boolean;
  created_at: string;
  updated_at: string;
}

export interface GetNavigationItemsParams {
  status?: 'active' | 'inactive';
  section?: 'main' | 'bottom';
  store_id?: string; // Optional: for dynamic badge counts
}

export interface NavigationBadgeCounts {
  inbox: number;
  support_tickets: number;
}

/**
 * Get badge counts for navigation items
 * GET /api/v1/navigation/badges?store_id=xxx
 * Returns raw counts (frontend handles formatting like "99+")
 */
export async function getNavigationBadges(storeId: string): Promise<NavigationBadgeCounts> {
  try {
    const res = await httpClient.get<NavigationBadgeCounts>(`/api/v1/navigation/badges?store_id=${storeId}`);
    if (res.success && res.data) {
      return res.data;
    }
    throw new ApiError(res.error || 'Failed to load navigation badges', 0);
  } catch (error) {
    throw error;
  }
}

export interface NavigationResponse {
  items: NavigationItem[];
  badge_counts?: NavigationBadgeCounts; // Only present if store_id was provided
}

/**
 * Get all navigation items
 * GET /api/v1/navigation?status=active&section=main&store_id=xxx
 * Returns: { items: NavigationItem[], badge_counts?: { inbox: number, support_tickets: number } }
 */
export async function getNavigationItems(params?: GetNavigationItemsParams): Promise<NavigationResponse> {
  try {
    const queryParams = new URLSearchParams();
    if (params?.status) {
      queryParams.append('status', params.status);
    }
    if (params?.section) {
      queryParams.append('section', params.section);
    }
    if (params?.store_id) {
      queryParams.append('store_id', params.store_id);
    }

    const queryString = queryParams.toString();
    const url = `/api/v1/navigation${queryString ? `?${queryString}` : ''}`;

    const res = await httpClient.get<NavigationResponse>(url);
    if (res.success && res.data) {
      return res.data;
    }
    throw new ApiError(res.error || 'Failed to load navigation items', 0);
  } catch (error) {
    throw error;
  }
}

/**
 * Get a single navigation item by ID
 * GET /api/v1/navigation/:id
 */
export async function getNavigationItem(id: string): Promise<NavigationItem> {
  try {
    const res = await httpClient.get<NavigationItem>(`/api/v1/navigation/${id}`);
    if (res.success && res.data) {
      return res.data;
    }
    throw new ApiError(res.error || 'Failed to load navigation item', 0);
  } catch (error) {
    throw error;
  }
}

/**
 * Create a new navigation item
 * POST /api/v1/navigation
 */
export async function createNavigationItem(item: Omit<NavigationItem, 'created_at' | 'updated_at'>): Promise<NavigationItem> {
  try {
    const res = await httpClient.post<NavigationItem>('/api/v1/navigation', item);
    if (res.success && res.data) {
      return res.data;
    }
    throw new ApiError(res.error || 'Failed to create navigation item', 0);
  } catch (error) {
    throw error;
  }
}

/**
 * Update a navigation item
 * PUT /api/v1/navigation/:id
 */
export async function updateNavigationItem(
  id: string,
  updates: Partial<Omit<NavigationItem, 'id' | 'created_at' | 'updated_at'>>
): Promise<NavigationItem> {
  try {
    const res = await httpClient.put<NavigationItem>(`/api/v1/navigation/${id}`, updates);
    if (res.success && res.data) {
      return res.data;
    }
    throw new ApiError(res.error || 'Failed to update navigation item', 0);
  } catch (error) {
    throw error;
  }
}

/**
 * Update navigation item status
 * PATCH /api/v1/navigation/:id/status
 */
export async function updateNavigationItemStatus(id: string, status: 'active' | 'inactive'): Promise<NavigationItem> {
  try {
    const res = await httpClient.patch<NavigationItem>(`/api/v1/navigation/${id}/status`, { status });
    if (res.success && res.data) {
      return res.data;
    }
    throw new ApiError(res.error || 'Failed to update navigation item status', 0);
  } catch (error) {
    throw error;
  }
}

/**
 * Delete a navigation item
 * DELETE /api/v1/navigation/:id
 */
export async function deleteNavigationItem(id: string): Promise<void> {
  try {
    const res = await httpClient.delete(`/api/v1/navigation/${id}`);
    if (!res.success) {
      throw new ApiError(res.error || 'Failed to delete navigation item', 0);
    }
  } catch (error) {
    throw error;
  }
}

